/**
 * PreferencesContext
 * 
 * Thin wrapper around SessionContext for backwards compatibility.
 * All preferences logic is now handled by SessionContext.
 * 
 * This file exists to avoid breaking existing imports of:
 * - PreferencesProvider
 * - usePreferences
 * 
 * New code should use useSession() or useSessionPreferences() directly.
 */

import { ReactNode } from 'react';
import { useSession } from './SessionContext';

// Re-export types for backwards compatibility
export type { StoreInfo } from './SessionContext';

/**
 * PreferencesProvider is now a pass-through
 * SessionProvider handles everything
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  // Pass-through - SessionProvider handles everything
  return <>{children}</>;
}

/**
 * usePreferences hook - backwards compatible wrapper around useSession
 */
export function usePreferences() {
  const session = useSession();
  
  return {
    // Language
    language: session.language,
    setLanguage: session.setLanguage,
    
    // Store
    currentStore: session.currentStore,
    availableStores: session.availableStores,
    setStore: session.setStore,
    
    // Theme
    theme: session.theme,
    setTheme: session.setTheme,
    
    // Derived
    storeSlug: session.storeSlug,
    storeCurrency: session.storeCurrency,
    detectedCountry: session.detectedCountry,
    
    // Consent
    consent: session.consent,
    
    // Loading
    isLoading: session.isLoading,
    
    // Refetch
    refetch: session.refetch,
  };
}
